import {
  firstDefinedFromObj,
  formatNumericString,
  mergeNonEmptyValues,
  toKeyValue,
  toStr,
  toStringArray,
} from "./object-value-utils.js";
import {
  EQUIPMENT_INFO_MAP,
  OPERATING_CONDITIONS_MAP,
} from "./vitalsigns-maps.js";

/**
 * Normalizes Enalysis VitalSigns API response for the optimization UI.
 * @param {unknown} vitalSignsData - `response.data` from getVitalSigns (flat object per API contract)
 * @param {unknown} [reportData] - `response.data` from getEnalysisReport (report root)
 * @param {unknown} [fieldData] - `report.fieldData`; merged into operating-conditions flat source (report → fieldData → vitalSigns)
 * @param {unknown} [modulesData] - Merged into Equipment Info
 * @param {unknown} [framesData] - Merged into Equipment Info
 * @returns {ReturnType<typeof empty> & { vitalSigns: Array<{name:string, value: unknown, unit: string, subtitle: string}> }}
 */
export function normalizeVitalSignsResponse(
  vitalSignsData,
  reportData,
  fieldData,
  modulesData,
  framesData
) {
  if (
    !vitalSignsData
    || typeof vitalSignsData !== "object"
    || Array.isArray(vitalSignsData)
  ) {
    return empty();
  }

  const vitalSigns = buildVitalSignsFromFlatObject(vitalSignsData);

  return {
    vitalSigns,
    recommendedActions: toStringArray(vitalSignsData.recommendedActions),
    severityLevel: toStr(vitalSignsData.severityLevel),
    flagDiagnostics: toFlagDiagnostics(
      vitalSignsData.assetFlags,
      vitalSignsData
    ),
    operatingConditions: normalizeOperatingConditions(
      reportData,
      fieldData,
      vitalSignsData
    ),
    equipmentInfo: normalizeEquipmentInfo(
      vitalSignsData,
      modulesData,
      framesData
    ),
    detechtionCompressorNum: toStr(vitalSignsData.detechtionCompressorNum),
    legalLocation: toStr(vitalSignsData.legalLocation),
    unitConfigurationImperial: toStr(vitalSignsData.unitConfigurationImperial),
    compressorName: toStr(vitalSignsData.compressorName),
    lastReportDate: toStr(vitalSignsData.lastReportDate),
  };
}

function empty() {
  return {
    vitalSigns: [],
    recommendedActions: [],
    severityLevel: null,
    flagDiagnostics: {},
    operatingConditions: {},
    equipmentInfo: {},
    detechtionCompressorNum: null,
    legalLocation: null,
    unitConfigurationImperial: null,
    compressorName: null,
    lastReportDate: null,
  };
}

// --- Operating conditions

function formatOperatingValue(val, unit) {
  const str = formatNumericString(val, 2);
  if (!str || !unit || (typeof val === "string" && /[a-zA-Z/%]/.test(str)))
    return str;
  return str + " " + unit;
}

function buildOperatingConditionsFromFlat(source) {
  const out = {};
  for (const row of OPERATING_CONDITIONS_MAP) {
    const keys = row.apiKeys ?? (row.apiKey ? [row.apiKey] : []);
    const val = firstDefinedFromObj(source, keys);
    if (val !== undefined) out[row.label] = formatOperatingValue(val, row.unit);
  }
  return out;
}

const LABEL_TO_UNIT = Object.fromEntries(
  OPERATING_CONDITIONS_MAP.map(({ label, unit }) => [label, unit]).filter(
    ([, u]) => u
  )
);

function applyDefaultUnits(conditions, labelToUnit) {
  const map = labelToUnit ?? LABEL_TO_UNIT;
  const out = {};
  for (const [label, value] of Object.entries(conditions)) {
    const unit = map[label];
    const str = String(value ?? "").trim();
    if (unit && str && !/[a-zA-Z/%"]/.test(str))
      out[label] = unit === '"' ? str + unit : str + " " + unit;
    else out[label] = value;
  }
  return out;
}

/**
 * Merge report → fieldData → vitalSigns; flat keys → labels; `operatingConditions` blob overlays.
 * Report/field roots are plain objects or null/undefined (not arrays).
 */
function normalizeOperatingConditions(reportData, fieldData, vitalSignsData) {
  const source = {
    ...reportData,
    ...fieldData,
    ...vitalSignsData,
  };
  return applyDefaultUnits(
    mergeNonEmptyValues(
      buildOperatingConditionsFromFlat(source),
      toKeyValue(source.operatingConditions)
    )
  );
}

// --- Equipment info

function formatEquipmentValue(val, unit) {
  const str = formatNumericString(val, 1);
  if (!str || !unit || (typeof val === "string" && /[a-zA-Z/%"]/.test(str)))
    return str;
  return str + (unit === '"' ? unit : " " + unit);
}

function buildEquipmentInfoFromFlat(source) {
  const out = {};
  for (const row of EQUIPMENT_INFO_MAP) {
    const keys = row.apiKeys ?? (row.apiKey ? [row.apiKey] : []);
    const val = firstDefinedFromObj(source, keys);
    if (val !== undefined) out[row.label] = formatEquipmentValue(val, row.unit);
  }
  return out;
}

const EQUIPMENT_LABEL_TO_UNIT = Object.fromEntries(
  EQUIPMENT_INFO_MAP.filter(({ unit }) => unit).map(({ label, unit }) => [
    label,
    unit,
  ])
);

function enrichEquipmentWithDefaults(info, source) {
  const withUnits = applyDefaultUnits(info, EQUIPMENT_LABEL_TO_UNIT);
  const frame =
    `${source.frameMake ?? ""} ${source.frameModel ?? ""}`.trim()
    || `${source.manufacturerName ?? ""} ${source.modelName ?? ""}`.trim();
  if (frame) withUnits["Frame"] = frame;

  const stagesStr = formatEquipmentValue(source.frameNumberOfStages, "");
  const throwsStr = formatEquipmentValue(source.frameNumberOfThrows, "");
  if (stagesStr) withUnits["Stages"] = stagesStr;
  if (throwsStr) withUnits["Throws"] = throwsStr;

  const driver =
    `${source.engineMake ?? ""} ${source.engineModel ?? ""}`.trim();
  if (driver) withUnits["Driver"] = driver;

  const min = source.minRPM;
  const max = source.maxRPM;
  if (min != null || max != null) {
    withUnits["RPM Range"] =
      min != null && max != null
        ? `${min} - ${max}`
        : formatEquipmentValue(min ?? max, "");
  }
  if (!withUnits["Max HP"] && source.maxPower != null)
    withUnits["Max HP"] = formatEquipmentValue(source.maxPower, "HP");
  if (!withUnits["Rod Load Type"] && source.rodLoadType)
    withUnits["Rod Load Type"] = formatEquipmentValue(source.rodLoadType, "");
  return withUnits;
}

/**
 * Merge vitalSigns → modules → frames; flat keys → labels; `equipmentInfo` blob overlays.
 * Modules/frames roots are plain objects or null/undefined (not arrays).
 */
function normalizeEquipmentInfo(vitalSignsData, modulesData, framesData) {
  const source = {
    ...vitalSignsData,
    ...modulesData,
    ...framesData,
  };
  return enrichEquipmentWithDefaults(
    mergeNonEmptyValues(
      buildEquipmentInfoFromFlat(source),
      toKeyValue(source.equipmentInfo)
    ),
    source
  );
}

/** VitalSigns `response.data` has many scalar metadata fields; only these map to the vitals cards. */
const VITAL_SIGN_CARD_KEYS = new Set([
  "cylinderUtilizationPercent",
  "hpUtilizationPercent",
  "runningSpeedRpm",
  "suctionPressure",
  "reportedFlow",
  "dischargePressure",
]);

function buildVitalSignsFromFlatObject(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return [];
  const out = [];
  for (const key of VITAL_SIGN_CARD_KEYS) {
    const val = obj[key];
    if (val == null) continue;
    if (typeof val === "number" || typeof val === "string") {
      out.push({ name: key, value: val, unit: "", subtitle: "" });
    }
  }
  return out;
}

// --- Asset flags (classification + flag diagnostics)

/** @typedef {"cylinders"|"engine"|"frame"|"cooler"} AssetFlagCategory */

/**
 * @typedef {Object} AssetFlag
 * @property {number|null|undefined} [stageNumber]
 * @property {number|null|undefined} [throwNumber]
 * @property {number|string} [severityLevel]
 * @property {string|null|undefined} [flagName]
 * @property {number|null|undefined} [value]
 * @property {number|null|undefined} [unitType]
 * @property {string|null|undefined} [trendName]
 */

const ENGINE_TRENDS = new Set([
  "lastOilChange",
  "topEndOverhaul",
  "topEndEngineOverhaulHours",
  "bottomEndOverhaul",
  "bottomEndEngineOverhaulHours",
  "speedRpm",
  "engineHorsepowerUsed",
  "horsepowerUtilized",
]);

const COOLER_TRENDS = new Set(["suctionTemperature"]);

/**
 * @param {AssetFlag} flag
 * @returns {AssetFlagCategory}
 */
export function classifyAssetFlag(flag) {
  const trend = (flag.trendName ?? "").trim();
  if (flag.stageNumber != null) {
    if (COOLER_TRENDS.has(trend)) return "cooler";
    return "cylinders";
  }
  if (ENGINE_TRENDS.has(trend)) return "engine";
  return "frame";
}

/**
 * @param {AssetFlag[]} flags
 * @returns {Record<AssetFlagCategory, AssetFlag[]>}
 */
export function groupFlagsByCategory(flags) {
  return flags.reduce(
    (acc, flag) => {
      acc[classifyAssetFlag(flag)].push(flag);
      return acc;
    },
    {
      cylinders: [],
      engine: [],
      frame: [],
      cooler: [],
    }
  );
}

const ASSET_FLAG_NESTED_BUCKET_KEYS = [
  "engine",
  "frame",
  "cooler",
  "compressor",
];

/**
 * @param {unknown} val - assetFlags from API
 * @param {Record<string, unknown>} data - full vitalSigns root for fallbacks
 * @returns {AssetFlag[]}
 */
function collectAllAssetFlags(val, data) {
  const seen = new WeakSet();
  /** @type {AssetFlag[]} */
  const out = [];
  function pushArray(arr) {
    if (!Array.isArray(arr)) return;
    for (const f of arr) {
      if (f == null || typeof f !== "object") continue;
      if (seen.has(f)) continue;
      seen.add(f);
      out.push(/** @type {AssetFlag} */ (f));
    }
  }
  if (Array.isArray(val)) {
    pushArray(val);
  } else if (val && typeof val === "object") {
    const v = val;
    pushArray(v.flags);
    if (v.cylinders && typeof v.cylinders === "object") {
      pushArray(v.cylinders.flags);
    }
    for (const k of ASSET_FLAG_NESTED_BUCKET_KEYS) {
      const bucket = v[k];
      if (bucket && typeof bucket === "object" && Array.isArray(bucket.flags)) {
        pushArray(bucket.flags);
      }
    }
  }
  if (data.cylinders && typeof data.cylinders === "object") {
    pushArray(data.cylinders.flags);
  }
  return out;
}

/** @param {unknown} s @returns {string|null} */
function normalizeSeverityForFlag(s) {
  if (s == null || s === "") return null;
  if (typeof s === "number") {
    if (s >= 2) return "Severe";
    if (s === 1) return "Warning";
    return String(s);
  }
  const t = String(s).trim();
  return t || null;
}

/**
 * @param {Array<{ severity?: string|null }>} formatted
 * @param {string} statusFallback
 */
function summarizeSevereWarningFlags(formatted, statusFallback) {
  const severeCount = formatted.filter(
    (f) => f.severity && /^severe$/i.test(String(f.severity).trim())
  ).length;
  const warningCount = formatted.filter(
    (f) => f.severity && /^warning$/i.test(String(f.severity).trim())
  ).length;
  const summary =
    (severeCount > 0 ? `${severeCount} Severe` : "")
      + (severeCount > 0 && warningCount > 0 ? ", " : "")
      + (warningCount > 0 ? `${warningCount} Warning` : "") || statusFallback;
  return { severeCount, warningCount, summary };
}

/**
 * @param {AssetFlag|Record<string, unknown>} item
 * @returns {{ text: string, severity: string|null, name: string|null }}
 */
function formatAssetFlagForDisplay(item) {
  if (item == null) return { text: "", severity: null, name: null };
  if (typeof item === "string")
    return { text: item.trim(), severity: null, name: null };
  if (typeof item !== "object" || Array.isArray(item))
    return { text: String(item), severity: null, name: null };
  const o = item;
  const flagName = toStr(o.flagName);
  const rawDesc = toStr(o.description);
  const trend = toStr(o.trendName) ?? "";
  const desc =
    rawDesc
    || (o.value != null && o.value !== "" ? String(o.value) : trend || "");
  const severity = normalizeSeverityForFlag(o.severityLevel);
  const stageNum =
    o.stageNumber != null && o.stageNumber !== "" ? String(o.stageNumber) : "";
  const throwNum =
    o.throwNumber != null && o.throwNumber !== "" ? String(o.throwNumber) : "";
  let text;
  if (stageNum && throwNum) {
    text = `S${stageNum}/T${throwNum}`;
  } else if (desc) {
    text = desc;
  } else {
    text = flagName || trend || JSON.stringify(o);
  }
  return { text, severity, name: flagName };
}

/**
 * @param {AssetFlag[]} rawItems
 * @returns {{ status: string, severeCount: number, warningCount: number, flags: ReturnType<typeof formatAssetFlagForDisplay>[] }|null}
 */
function buildFlagDiagnosticsSection(rawItems) {
  const formatted = rawItems
    .map((item) => formatAssetFlagForDisplay(item))
    .filter((f) => f.text.trim());
  if (formatted.length === 0) return null;
  const { severeCount, warningCount, summary } = summarizeSevereWarningFlags(
    formatted,
    "Active"
  );
  return { status: summary, severeCount, warningCount, flags: formatted };
}

/** Format a single cylinder flag: "S{stageNumber}/T{throwNumber} - {description}". */
function formatCylinderFlag(item) {
  if (item == null) return { text: "", severity: null, name: null };
  if (typeof item === "string")
    return { text: item.trim(), severity: null, name: null };
  if (typeof item !== "object" || Array.isArray(item))
    return { text: String(item), severity: null, name: null };
  const o = item;
  const flagName = toStr(o.flagName);
  const description = toStr(o.description) ?? "";
  const severity = toStr(o.severityLevel);
  const stageNum = toStr(o.stageNumber);
  const throwNum = toStr(o.throwNumber);
  let text;
  if (!stageNum && !throwNum) {
    text = description ? `Dummy Cylinder - ${description}` : "Dummy Cylinder";
  } else if (stageNum && throwNum && description) {
    text = `S${stageNum}/T${throwNum} - ${description}`;
  } else if (stageNum && throwNum) {
    text = `S${stageNum}/T${throwNum}`;
  } else {
    text = description || JSON.stringify(item);
  }
  return { text, severity, name: flagName };
}

/** Legacy path when `assetFlags` is not a flat merged list. */
function collectLegacyCylinderRawFlags(val, data) {
  let cylinders = null;
  let cylinderStatus = "Unknown";
  let rawFlags = [];

  if (Array.isArray(val) && val.length > 0) {
    rawFlags = val;
  } else if (val && typeof val === "object") {
    const v = val;
    cylinders = v.cylinders;
    if (cylinders && typeof cylinders === "object") {
      cylinderStatus = toStr(cylinders.status) ?? "Unknown";
      rawFlags = Array.isArray(cylinders.flags) ? cylinders.flags : [];
    }
    if (rawFlags.length === 0 && v.flags) {
      rawFlags = Array.isArray(v.flags) ? v.flags : [];
      cylinderStatus = toStr(v.status) ?? "Unknown";
    }
  }
  if (
    rawFlags.length === 0
    && data.cylinders
    && typeof data.cylinders === "object"
  ) {
    cylinderStatus = toStr(data.cylinders.status) ?? "Unknown";
    rawFlags = Array.isArray(data.cylinders.flags) ? data.cylinders.flags : [];
  }
  return { cylinders, cylinderStatus, rawFlags };
}

const FLAG_DIAGNOSTIC_GROUPS = [
  ["cylinders", "cylinders"],
  ["engine", "engine"],
  ["frame", "frame"],
  ["cooler", "cooler"],
];

function toFlagDiagnostics(val, d = {}) {
  const data = typeof d === "object" && d !== null ? d : {};
  /** @type {Record<string, unknown>} */
  const out = {};

  const allFlags = collectAllAssetFlags(val, data);

  if (allFlags.length > 0) {
    const grouped = groupFlagsByCategory(allFlags);
    for (const [outKey, groupKey] of FLAG_DIAGNOSTIC_GROUPS) {
      const section = buildFlagDiagnosticsSection(grouped[groupKey]);
      if (section) out[outKey] = section;
    }
  } else {
    const { cylinders, cylinderStatus, rawFlags } =
      collectLegacyCylinderRawFlags(val, data);
    const formatted = rawFlags
      .map((item) => formatCylinderFlag(item))
      .filter((f) => f.text.trim());
    if (cylinders != null || formatted.length > 0) {
      const { severeCount, warningCount, summary } =
        summarizeSevereWarningFlags(formatted, cylinderStatus);
      out.cylinders = {
        status: summary,
        severeCount,
        warningCount,
        flags: formatted,
      };
    }
  }

  const valObj =
    val && typeof val === "object" && !Array.isArray(val) ? val : null;
  for (const key of ["engine", "frame", "cooler"]) {
    if (out[key] != null) continue;
    const resolved = (valObj ? toStr(valObj[key]) : null) ?? toStr(data[key]);
    if (resolved != null) out[key] = resolved;
  }

  return out;
}
